import React, { useEffect, useState } from "react";
import { PageHeader, DataTable, StatusBadge } from "@/components/common";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function AdSizesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", width: 300, height: 250 });

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/admin/ad-sizes"); setRows(data); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    try {
      const body = { ...form, width: parseInt(form.width, 10), height: parseInt(form.height, 10), name: form.name || `${form.width}x${form.height}` };
      await api.post("/admin/ad-sizes", body);
      toast.success("Ölçü eklendi");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }
  async function remove(r) {
    if (!window.confirm(`${r.name} silinsin mi?`)) return;
    try { await api.delete(`/admin/ad-sizes/${r.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        overline="Yapılandırma"
        title="Reklam Ölçüleri"
        description="Sistem genelinde kullanılabilen reklam boyutları."
        actions={
          <Button className="bg-red-600 hover:bg-red-700 rounded-sm h-10 font-heading font-semibold uppercase text-xs tracking-wider" onClick={() => setOpen(true)} data-testid="add-size-button">
            <Plus className="w-4 h-4 mr-1.5" /> Yeni Ölçü
          </Button>
        }
      />

      {loading ? <div className="text-sm text-slate-500">Yükleniyor…</div> : (
        <DataTable
          testid="sizes-table"
          columns={[
            { label: "Adı", render: (r) => <span className="font-mono font-medium">{r.name}</span> },
            { label: "Genişlik", render: (r) => `${r.width}px` },
            { label: "Yükseklik", render: (r) => `${r.height}px` },
            { label: "Durum", render: (r) => <StatusBadge status={r.status} /> },
            {
              label: "İşlemler", render: (r) => (
                <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => remove(r)} data-testid={`delete-size-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
              )
            },
          ]}
          rows={rows}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Yeni Reklam Ölçüsü</DialogTitle>
            <DialogDescription>IAB standardında yeni bir reklam banner ölçüsü tanımlayın (genişlik × yükseklik, piksel).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>İsim</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="600x300" data-testid="size-form-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Genişlik (px)</Label><Input type="number" value={form.width} onChange={(e) => setForm({ ...form, width: e.target.value })} data-testid="size-form-w" /></div>
              <div><Label>Yükseklik (px)</Label><Input type="number" value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} data-testid="size-form-h" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} className="bg-red-600 hover:bg-red-700 rounded-sm" data-testid="size-form-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
